import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { naira } from '@/lib/format';
import { getChildren, getResultsAnnual, getResultsById, getResultsTermsById, getResultsTranscript, postResultsTranscriptRequest } from '@/api/parent';
import { Banner, type ChildCard, Empty, ErrorBox, Loading, PageTitle, Scroll, errMsg, field, unwrap } from '../ui';
import { saveFromApi } from '@/lib/download';
import { Download } from 'lucide-react';
import { TemplateSheet } from '@/features/shared/results/TemplateSheet';

interface Hold { reason: string; message: string; owed: number | null; note?: string | null }
interface TermRow { result_id: number; term_id: number; term: string; session: string | null; status: string; average: number | null; position: number | null; class_size: number | null; grade: string | null; hold?: Hold }
interface Sheet {
  status?: string; hold?: Hold; class?: string; term?: string;
  columns?: { code: string; name: string; max: number }[];
  subjects?: { subject_id: number; subject: string; scores: Record<string, number> | null; total: number; grade: string; remark: string | null; position: number | null; class_average: number | null }[];
  summary?: { total: number; average: number; grade: string; position: number | null; class_size: number | null; attendance_present: number | null; attendance_total: number | null };
  comments?: { teacher: string | null; principal: string | null }; next_term_begins?: string | null;
  grading_key?: { grade: string; from: number; to: number; remark: string }[];
}
interface Annual { session: string | null; status: string; hold?: Hold; total?: number; average?: number; position?: number | null; grade?: string; promotion_status?: string | null }
interface Transcript { student: { name: string; admission_no: string }; sessions: { session: string | null; annual: { average: number; position: number | null; grade: string; promotion_status: string | null } | null; terms: { term: string; class: string; subjects: Sheet['subjects']; summary: Sheet['summary'] }[] }[] }

const HoldNotice = ({ h }: { h?: Hold }) => (
  <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 text-sm text-purple-900 space-y-1">
    <p className="font-semibold">{h?.message ?? 'These results are being held.'}</p>
    {h?.note && <p>Message from the school: “{h.note}”</p>}
    {h?.owed ? <p>Outstanding: <b>{naira(h.owed)}</b>. <a href="/parent/fees" className="underline font-semibold">Pay now</a> — the result opens once fees are cleared.</p> : null}
  </div>
);

function DownloadSheet({ studentId, termId, label = 'Download report sheet' }: { studentId: string; termId: number; label?: string }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  return (
    <span className="inline-flex flex-col items-start">
      <Button variant="ghost" disabled={busy} onClick={async () => { setBusy(true); setErr(''); try { await saveFromApi(`/parent/results/${studentId}/terms/${termId}/file`, 'report-sheet.pdf'); } catch (e: any) { setErr(e.message); } finally { setBusy(false); } }}>
        <Download className="w-4 h-4 mr-1 inline" />{busy ? 'Preparing…' : label}
      </Button>
      {err && <span className="text-xs text-red-600 mt-1 max-w-xs">{err}</span>}
    </span>
  );
}

function SheetView({ studentId, termId }: { studentId: string; termId: number }) {
  const q = useQuery({ queryKey: ['parent-result-term', studentId, termId], queryFn: () => unwrap<Sheet>(getResultsTermsById(studentId, termId)) });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const s = q.data;
  if (s.status === 'held') return <HoldNotice h={s.hold} />;
  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap items-center justify-between gap-2"><p className="text-slate-500">{s.class} · {s.term}</p><DownloadSheet studentId={studentId} termId={termId} /></div>
      <TemplateSheet url={`/parent/results/${studentId}/terms/${termId}/sheet`} fallback={<SheetTable s={s} />} />
    </div>
  );
}

function SheetTable({ s }: { s: Sheet }) {
  return (
    <div className="space-y-4 text-sm">
      <Scroll><table className="w-full min-w-[560px]">
        <thead><tr className="text-left text-slate-500"><th className="py-1">Subject</th>{s.columns?.map((c) => <th key={c.code}>{c.name} ({c.max})</th>)}<th>Total</th><th>Grade</th><th>Pos.</th><th>Class avg</th><th>Remark</th></tr></thead>
        <tbody>
          {s.subjects?.map((x) => (
            <tr key={x.subject_id} className="border-t">
              <td className="py-1.5">{x.subject}</td>{s.columns?.map((c) => <td key={c.code}>{x.scores?.[c.code] ?? '—'}</td>)}
              <td className="font-semibold">{x.total}</td><td>{x.grade}</td><td>{x.position ?? '—'}</td><td>{x.class_average ?? '—'}</td><td>{x.remark}</td>
            </tr>
          ))}
        </tbody>
      </table></Scroll>
      {s.summary && <p>Total <b>{s.summary.total}</b> · Average <b>{s.summary.average}</b> · Grade <b>{s.summary.grade}</b> · Position <b>{s.summary.position ?? '—'}</b>/{s.summary.class_size ?? '—'}{s.summary.attendance_total != null && <> · Attendance {s.summary.attendance_present}/{s.summary.attendance_total}</>}</p>}
      {s.comments?.teacher && <p><span className="text-slate-500">Teacher:</span> {s.comments.teacher}</p>}
      {s.comments?.principal && <p><span className="text-slate-500">Principal:</span> {s.comments.principal}</p>}
      {s.next_term_begins && <p className="text-slate-500">Next term begins {s.next_term_begins}</p>}
    </div>
  );
}

export function ResultsPage() {
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState<'terms' | 'annual' | 'transcript'>('terms');
  const [term, setTerm] = useState<number | null>(null);
  const [copies, setCopies] = useState('1');
  const [msg, setMsg] = useState<{ tone: 'success' | 'error'; text: string } | null>(null);

  const kids = useQuery({ queryKey: ['parent-children'], queryFn: () => unwrap<ChildCard[]>(getChildren()) });
  const studentId = params.get('student') ?? (kids.data?.[0] ? String(kids.data[0].student_id) : '');
  const on = !!studentId;
  const terms = useQuery({ queryKey: ['parent-results', studentId], queryFn: () => unwrap<TermRow[]>(getResultsById(studentId)), enabled: on && tab === 'terms' });
  const annual = useQuery({ queryKey: ['parent-results-annual', studentId], queryFn: () => unwrap<Annual[]>(getResultsAnnual(studentId)), enabled: on && tab === 'annual' });
  const transcript = useQuery({ queryKey: ['parent-transcript', studentId], queryFn: () => unwrap<Transcript>(getResultsTranscript(studentId)), enabled: on && tab === 'transcript', retry: false });
  const request = useMutation({
    mutationFn: () => postResultsTranscriptRequest(studentId, { copies: Number(copies) || 1 }),
    onSuccess: (r) => setMsg({ tone: 'success', text: (r as { data: { message?: string } }).data.message ?? 'Request received.' }),
    onError: (e) => setMsg({ tone: 'error', text: errMsg(e) }),
  });

  const tabBtn = (t: typeof tab, text: string) => <Button variant={tab === t ? 'primary' : 'ghost'} onClick={() => { setTab(t); setTerm(null); }}>{text}</Button>;

  return (
    <div className="space-y-5">
      <PageTitle kicker="My children" title="Results" sub="Term results, report sheets, annual summaries and transcripts." />
      {msg && <Banner tone={msg.tone} onClose={() => setMsg(null)}>{msg.text}</Banner>}
      {kids.isLoading ? <Loading /> : kids.isError ? <ErrorBox error={kids.error} onRetry={() => kids.refetch()} /> : !kids.data?.length ? <Card><Empty text="No children linked yet." /></Card> : (
        <>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
            <select className={`${field} sm:!w-64`} value={studentId} onChange={(e) => { setParams({ student: e.target.value }); setTerm(null); }}>
              {kids.data.map((c) => <option key={c.student_id} value={c.student_id}>{c.name}</option>)}
            </select>
            <div className="flex gap-2">{tabBtn('terms', 'Terms')}{tabBtn('annual', 'Annual')}{tabBtn('transcript', 'Transcript')}</div>
          </div>

          {tab === 'terms' && (term ? (
            <Card title="Term result" action={<Button variant="ghost" onClick={() => setTerm(null)}>Back</Button>}><SheetView studentId={studentId} termId={term} /></Card>
          ) : (
            <Card title="Term results">
              {terms.isLoading ? <Loading /> : terms.isError ? <ErrorBox error={terms.error} onRetry={() => terms.refetch()} /> : !terms.data?.length ? <Empty text="No published results yet." /> : (
                <Scroll><table className="w-full text-sm min-w-[460px]">
                  <thead><tr className="text-left text-slate-500"><th className="py-1">Session</th><th>Term</th><th>Average</th><th>Grade</th><th>Position</th><th /></tr></thead>
                  <tbody>
                    {terms.data.map((t) => (
                      <tr key={t.result_id} className="border-t">
                        <td className="py-2">{t.session}</td><td>{t.term}</td>
                        {t.hold ? <td colSpan={3}><StatusBadge status="held" /></td> : <><td>{t.average}</td><td>{t.grade}</td><td>{t.position}/{t.class_size}</td></>}
                        <td className="text-right whitespace-nowrap"><Button variant="ghost" onClick={() => setTerm(t.term_id)}>{t.hold ? 'Why?' : 'View'}</Button>{!t.hold && <DownloadSheet studentId={studentId} termId={t.term_id} label="PDF" />}</td>
                      </tr>
                    ))}
                  </tbody>
                </table></Scroll>
              )}
            </Card>
          ))}

          {tab === 'annual' && (
            <Card title="Annual results">
              {annual.isLoading ? <Loading /> : annual.isError ? <ErrorBox error={annual.error} onRetry={() => annual.refetch()} /> : !annual.data?.length ? <Empty text="No annual results yet." /> : (
                <Scroll><table className="w-full text-sm min-w-[460px]">
                  <thead><tr className="text-left text-slate-500"><th className="py-1">Session</th><th>Total</th><th>Average</th><th>Grade</th><th>Position</th><th>Promotion</th></tr></thead>
                  <tbody>
                    {annual.data.map((a, i) => a.status === 'held' ? <tr key={i} className="border-t"><td className="py-2">{a.session}</td><td colSpan={5}><HoldNotice h={a.hold} /></td></tr> : (
                      <tr key={i} className="border-t"><td className="py-2">{a.session}</td><td>{a.total}</td><td>{a.average}</td><td>{a.grade}</td><td>{a.position ?? '—'}</td><td>{a.promotion_status ?? '—'}</td></tr>
                    ))}
                  </tbody>
                </table></Scroll>
              )}
            </Card>
          )}

          {tab === 'transcript' && (
            <>
              <Card title="Transcript">
                {transcript.isLoading ? <Loading /> : transcript.isError || !transcript.data ? <ErrorBox error={transcript.error} onRetry={() => transcript.refetch()} /> : !transcript.data.sessions.length ? <Empty text="No transcript data yet." /> : (
                  <div className="space-y-4 text-sm">
                    {transcript.data.sessions.map((s, i) => (
                      <div key={i}>
                        <div className="font-semibold">{s.session}{s.annual && <span className="font-normal text-slate-500"> — average {s.annual.average}, grade {s.annual.grade}, position {s.annual.position ?? '—'}, {s.annual.promotion_status ?? ''}</span>}</div>
                        {s.terms.map((t, j) => <div key={j} className="text-slate-600 ml-3">{t.term} · {t.class} · average {t.summary?.average ?? '—'} · grade {t.summary?.grade ?? '—'}</div>)}
                      </div>
                    ))}
                  </div>
                )}
              </Card>
              <Card title="Request an official transcript">
                <p className="text-sm text-slate-500 mb-3">The school prepares an official copy. A fee may apply.</p>
                <div className="flex flex-wrap gap-3 items-center">
                  <label className="text-sm">Copies</label>
                  <input className={`${field} !w-20`} inputMode="numeric" value={copies} onChange={(e) => setCopies(e.target.value.replace(/\D/g, '').slice(0, 2))} />
                  <Button disabled={request.isPending || !Number(copies)} onClick={() => request.mutate()}>{request.isPending ? 'Sending…' : 'Request transcript'}</Button>
                </div>
              </Card>
            </>
          )}
        </>
      )}
    </div>
  );
}
